import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, isToday } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarWidget = ({ 
  events = [], 
  onDateSelect, 
  onEventCreate, 
  className = '',
  leads = []
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, agenda
  const [showEventModal, setShowEventModal] = useState(false);

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const calendarDays = [];
  let day = startDate;

  while (day <= endDate) {
    calendarDays?.push(day);
    day = addDays(day, 1);
  }

  // Mock events based on leads and activities
  const calendarEvents = [
    {
      id: 1,
      title: 'Follow-up Call: Sarah Johnson',
      date: new Date(),
      time: '10:30 AM',
      type: 'call',
      leadId: 1,
      priority: 'high'
    },
    {
      id: 2,
      title: 'Demo: TechCorp Solutions',
      date: addDays(new Date(), 1),
      time: '2:00 PM',
      type: 'meeting',
      leadId: 1,
      priority: 'high'
    },
    {
      id: 3,
      title: 'Email Follow-up: Michael Chen',
      date: addDays(new Date(), 2),
      time: '9:00 AM',
      type: 'email',
      leadId: 2,
      priority: 'medium'
    },
    {
      id: 4,
      title: 'Proposal Review: CloudTech',
      date: addDays(new Date(), 3),
      time: '3:30 PM',
      type: 'meeting',
      leadId: 3,
      priority: 'high'
    },
    {
      id: 5,
      title: 'Check-in Call: David Wilson',
      date: addDays(new Date(), 5),
      time: '11:00 AM',
      type: 'call',
      leadId: 4,
      priority: 'low'
    }
  ];

  const getEventsForDate = (date) => {
    return calendarEvents?.filter(event => 
      isSameDay(event?.date, date)
    );
  };

  const getTodaysEvents = () => {
    return getEventsForDate(new Date());
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    return calendarEvents
      ?.filter(event => event?.date > now)
      ?.sort((a, b) => a?.date - b?.date)
      ?.slice(0, 3);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleEventCreate = (date) => {
    onEventCreate?.({ date, time: '09:00' });
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'call': return 'Phone';
      case 'meeting': return 'Users';
      case 'email': return 'Mail';
      default: return 'Calendar';
    }
  };

  const getEventTypeColor = (type, priority) => {
    const baseColors = {
      call: 'bg-blue-500',
      meeting: 'bg-green-500',
      email: 'bg-purple-500'
    };
    
    const priorityIntensity = {
      high: 'opacity-100',
      medium: 'opacity-75',
      low: 'opacity-50'
    };

    return `${baseColors?.[type] || 'bg-gray-500'} ${priorityIntensity?.[priority] || 'opacity-75'}`;
  };

  if (viewMode === 'agenda') {
    return (
      <div className={`bg-card border border-border rounded-lg ${className}`}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground flex items-center">
              <Icon name="Calendar" size={20} className="mr-2 text-primary" />
              Activity Calendar
            </h3>
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'month' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setViewMode('month')}
                className={viewMode === 'month' ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
              >
                Month
              </Button>
              <Button
                variant={viewMode === 'agenda' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setViewMode('agenda')}
                className={viewMode === 'agenda' ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
              >
                Agenda
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Today's Events */}
          <div>
            <h4 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="Clock" size={16} className="mr-2 text-success" />
              Today's Activities
            </h4>
            {getTodaysEvents()?.length === 0 ? (
              <p className="text-muted-foreground text-sm">No activities scheduled for today</p>
            ) : (
              <div className="space-y-2">
                {getTodaysEvents()?.map(event => (
                  <div key={event?.id} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event?.type, event?.priority)}`} />
                    <Icon name={getEventTypeIcon(event?.type)} size={16} className="text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground text-sm">{event?.title}</p>
                      <p className="text-muted-foreground text-xs">{event?.time}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => console.log('Edit event', event?.id)}>
                        <Icon name="Edit" size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div>
            <h4 className="font-medium text-foreground mb-3 flex items-center">
              <Icon name="TrendingUp" size={16} className="mr-2 text-warning" />
              Upcoming Activities
            </h4>
            {getUpcomingEvents()?.length === 0 ? (
              <p className="text-muted-foreground text-sm">No upcoming activities</p>
            ) : (
              <div className="space-y-2">
                {getUpcomingEvents()?.map(event => (
                  <div key={event?.id} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event?.type, event?.priority)}`} />
                    <Icon name={getEventTypeIcon(event?.type)} size={16} className="text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground text-sm">{event?.title}</p>
                      <p className="text-muted-foreground text-xs">
                        {format(event?.date, 'MMM dd')} at {event?.time}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => console.log('Edit event', event?.id)}>
                        <Icon name="Edit" size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEventCreate(new Date())}
                className="flex-1"
              >
                <Icon name="Plus" size={14} className="mr-1" />
                Schedule Call
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEventCreate(new Date())}
                className="flex-1"
              >
                <Icon name="Users" size={14} className="mr-1" />
                Book Meeting
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="Calendar" size={20} className="mr-2 text-primary" />
            Activity Calendar
          </h3>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('month')}
              className={viewMode === 'month' ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
            >
              Month
            </Button>
            <Button
              variant={viewMode === 'agenda' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('agenda')}
              className={viewMode === 'agenda' ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
            >
              Agenda
            </Button>
          </div>
        </div>
      </div>
      <div className="p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={handlePrevMonth}>
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <h4 className="text-lg font-semibold text-foreground">
              {format(currentDate, 'MMMM yyyy')}
            </h4>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays?.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isSelected = isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isDayToday = isToday(day);

            return (
              <div
                key={index}
                onClick={() => handleDateClick(day)}
                className={`
                  relative p-2 min-h-[60px] cursor-pointer rounded-lg border transition-colors
                  ${isSelected 
                    ? 'bg-primary text-primary-foreground border-primary' 
                    : 'border-transparent hover:bg-muted'
                  }
                  ${!isCurrentMonth ? 'text-muted-foreground' : 'text-foreground'}
                  ${isDayToday && !isSelected ? 'bg-accent text-accent-foreground font-semibold' : ''}
                `}
              >
                <div className="text-sm">{format(day, 'd')}</div>
                
                {/* Event indicators */}
                {dayEvents?.length > 0 && (
                  <div className="absolute bottom-1 left-1 right-1">
                    <div className="flex items-center space-x-1">
                      {dayEvents?.slice(0, 3)?.map((event, eventIndex) => (
                        <div
                          key={eventIndex}
                          className={`w-2 h-2 rounded-full ${getEventTypeColor(event?.type, event?.priority)}`}
                          title={event?.title}
                        />
                      ))}
                      {dayEvents?.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{dayEvents?.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Selected Date Events */}
        {getEventsForDate(selectedDate)?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h5 className="font-medium text-foreground mb-2 flex items-center">
              <Icon name="Clock" size={16} className="mr-2" />
              {format(selectedDate, 'MMMM dd')} Activities
            </h5>
            <div className="space-y-2">
              {getEventsForDate(selectedDate)?.map(event => (
                <div key={event?.id} className="flex items-center space-x-3 p-2 bg-muted rounded">
                  <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event?.type, event?.priority)}`} />
                  <Icon name={getEventTypeIcon(event?.type)} size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground text-sm">{event?.title}</p>
                    <p className="text-muted-foreground text-xs">{event?.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span>Calls</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Meetings</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span>Emails</span>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs"
              onClick={() => handleEventCreate(selectedDate)}
            >
              <Icon name="Plus" size={12} className="mr-1" />
              Add Activity
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;